import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { UserCircle, MagnifyingGlass, MapPin, Storefront, UsersThree } from "@phosphor-icons/react";

const LIMIT = 40;

// Popular searches shown as clickable chips below the search bar.
const POPULAR_SEARCHES = [
  { label: "Mulhouse", q: "", city: "Mulhouse" },
  { label: "Colmar", q: "", city: "Colmar" },
  { label: "Guebwiller", q: "", city: "Guebwiller" },
  { label: "Dupont", q: "Dupont", city: "" },
  { label: "Martin", q: "Martin", city: "" },
  { label: "Schmitt", q: "Schmitt", city: "" },
];

/**
 * WhitePages — VAGUE 28 + VAGUE 37C.
 *
 * Search by personal name (auto-entrepreneurs, EI, freelancers).
 * Complements the Pages Jaunes (Directory) which searches by activity.
 *
 * Hits `GET /api/public/whitepages?q=&city=&limit=&skip=`.
 */
export default function WhitePages() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(null);  // Count of ALL referenced persons
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));
  const [loading, setLoading] = useState(false);

  // Fetch the grand total once on mount so we can show the hero counter.
  useEffect(() => {
    api.get("/public/whitepages", { params: { limit: 1 } })
      .then((r) => setGrandTotal(r?.data?.total ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const skip = (page - 1) * LIMIT;
      const params = { limit: LIMIT, skip };
      if (q) params.q = q;
      if (city) params.city = city;
      try {
        const r = await api.get("/public/whitepages", { params });
        if (cancelled) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch (e) {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        // eslint-disable-next-line no-console
        console.error("[whitepages] fetch failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [q, city, page]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (city) next.set("city", city);
    setParams(next, { replace: true });
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="whitepages-root">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Link to="/" className="hover:text-primary">Accueil</Link>
          <span>›</span>
          <span>Pages Blanches</span>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <UserCircle size={32} weight="duotone" className="text-primary" />
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-primary">
            Trouver un professionnel en Alsace
          </h1>
        </div>
        <p className="text-base text-muted-foreground max-w-2xl">
          Recherchez un indépendant du Haut-Rhin par son <b>nom</b> ou son <b>prénom</b>&nbsp;:
          auto-entrepreneurs, artisans, professions libérales, consultants…
        </p>

        {/* VAGUE 37C — Hero counter */}
        {grandTotal !== null && grandTotal > 0 && (
          <div
            className="mt-4 inline-flex items-center gap-2 bg-primary/5 border border-primary/20 text-primary px-3 py-1.5 rounded-sm text-sm font-semibold"
            data-testid="whitepages-grand-total"
          >
            <UsersThree size={16} weight="duotone" />
            <span className="tabular-nums">{grandTotal.toLocaleString("fr-FR")}</span>
            professionnels référencés dans le Haut-Rhin
          </div>
        )}

        <div className="mt-3 inline-flex items-center gap-2 text-xs bg-accent/40 text-foreground px-3 py-1.5 rounded-sm">
          <Storefront size={14} weight="bold" className="text-secondary" />
          Vous cherchez un commerce ou un service ?{" "}
          <Link to="/annuaire" className="font-semibold text-primary hover:underline">
            Consulter les Pages Jaunes →
          </Link>
        </div>
      </div>

      {/* Search bar */}
      <form
        onSubmit={applySearch}
        className="bg-card border border-border rounded-sm p-4 mb-6 grid sm:grid-cols-[1fr_180px_120px] gap-3"
      >
        <div className="relative">
          <MagnifyingGlass
            size={18}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, prénom ou les deux (ex : Marie Dupont)"
            className="w-full pl-10 pr-3 py-2.5 text-sm border border-border rounded-sm bg-background focus:border-primary focus:outline-none"
            data-testid="whitepages-search-name"
          />
        </div>
        <div className="relative">
          <MapPin
            size={18}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ville (optionnel)"
            className="w-full pl-10 pr-3 py-2.5 text-sm border border-border rounded-sm bg-background focus:border-primary focus:outline-none"
            data-testid="whitepages-search-city"
          />
        </div>
        <button
          type="submit"
          className="bg-primary text-primary-foreground hover:bg-[#0f3d29] rounded-sm text-sm font-bold px-4 py-2.5"
          data-testid="whitepages-search-submit"
        >
          Rechercher
        </button>
      </form>

      {/* VAGUE 37C — Popular searches chips */}
      {!q && !city && (
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="whitepages-popular">
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mr-1">
            Suggestions&nbsp;:
          </span>
          {POPULAR_SEARCHES.map((p) => (
            <button
              key={`${p.q}-${p.city}`}
              onClick={() => {
                setQ(p.q); setCity(p.city); setPage(1);
                const nxt = new URLSearchParams();
                if (p.q) nxt.set("q", p.q);
                if (p.city) nxt.set("city", p.city);
                setParams(nxt, { replace: true });
              }}
              className="inline-flex items-center gap-1 text-xs font-semibold border border-border bg-card hover:border-primary hover:bg-primary/5 px-3 py-1.5 rounded-full transition-colors"
              data-testid={`whitepages-pop-${p.q || p.city}`}
            >
              {p.city ? <MapPin size={10} weight="bold" /> : <UserCircle size={10} weight="bold" />}
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-sm text-muted-foreground" data-testid="whitepages-total">
          {loading ? "Recherche en cours…" : `${total.toLocaleString("fr-FR")} personne${total > 1 ? "s" : ""} trouvée${total > 1 ? "s" : ""}`}
        </div>
        {pages > 1 && (
          <div className="text-xs text-muted-foreground">
            Page {page}/{pages}
          </div>
        )}
      </div>

      {!loading && items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-8 text-center text-muted-foreground">
          <UserCircle size={32} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
          Aucun résultat. Essayez avec un autre nom ou sans filtrer par ville.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="whitepages-list">
          {items.map((it) => (
            <li key={it.id} data-testid={`whitepages-item-${it.slug}`}>
              <Link
                to={`/entreprise/${it.slug}`}
                className="flex items-start gap-3 bg-card border border-border rounded-sm p-3 hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <UserCircle
                  size={40}
                  weight="duotone"
                  className={`shrink-0 mt-0.5 ${it["premium"]?.tier ? "text-secondary" : "text-primary/60"}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="font-semibold text-foreground truncate">
                      {it.name}
                    </div>
                    {it["premium"]?.tier && it["premium"].tier !== "free" && (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-secondary bg-secondary/10 border border-secondary/30 px-1.5 py-0.5 rounded-sm">
                        Premium
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[it.category_label, it.city].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {(it.rating_count > 0 || it.reviews_count > 0) && (it.rating_avg != null || it.avg_rating != null) && (
                  <span
                    className="text-sm font-semibold text-secondary shrink-0"
                    data-testid={`whitepages-item-rating-${it.slug}`}
                  >
                    ★ {Number(it.rating_avg ?? it.avg_rating ?? it.rating).toFixed(1)}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1.5 text-sm rounded-sm border border-border bg-card disabled:opacity-40 hover:bg-primary/5"
            data-testid="whitepages-prev"
          >
            ← Précédent
          </button>
          <button
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1.5 text-sm rounded-sm border border-border bg-card disabled:opacity-40 hover:bg-primary/5"
            data-testid="whitepages-next"
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}
